import React, { useState, useEffect, useCallback } from 'react';
import { Bell, Save, Play, CheckCircle, XCircle, Clock, AlertTriangle, RefreshCw, ChevronLeft, ChevronRight } from 'lucide-react';
import axios from 'axios';
import { AlertPreferences, AlertRecord } from '../types';
import { useAuth } from '../context/AuthContext';

const DEFAULT_PREFS: Omit<AlertPreferences, 'userId'> = {
  enabled: false,
  lookbackDays: 1,
  unsubscribePerSend: { enabled: true, threshold: 10 },
  unsubscribeDomain:  { enabled: true, threshold: 10 },
  bounceRate:         { enabled: true, threshold: 10 },
  stuckSend:          { enabled: false, thresholdHours: 24 },
  pausedSend:         { enabled: false },
};

const TYPE_LABELS: Record<AlertRecord['type'], string> = {
  unsubscribe_per_send: 'Unsubscribes / Send',
  unsubscribe_domain:   'Domain Cluster',
  bounce_rate:          'Bounce Rate',
  stuck_send:           'Stuck Send',
  paused_send:          'Paused Send',
};

const TYPE_COLORS: Record<AlertRecord['type'], string> = {
  unsubscribe_per_send: 'bg-orange-100 text-orange-800',
  unsubscribe_domain:   'bg-purple-100 text-purple-800',
  bounce_rate:          'bg-red-100 text-red-800',
  stuck_send:           'bg-amber-100 text-amber-800',
  paused_send:          'bg-blue-100 text-blue-800',
};

export default function Alerts() {
  const { user } = useAuth();
  const [prefs, setPrefs] = useState<AlertPreferences>({ ...DEFAULT_PREFS, userId: user?.id ?? '' });
  const [history, setHistory] = useState<AlertRecord[]>([]);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [running, setRunning] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [historyPage, setHistoryPage] = useState(1);
  const [alertRunHour, setAlertRunHour] = useState<number | null>(null);

  const PAGE_SIZE = 20;

  const fetchPrefs = useCallback(async () => {
    try {
      const res = await axios.get<AlertPreferences>('/api/alerts/preferences');
      setPrefs(res.data);
    } catch (err) {
      console.error('Failed to load alert preferences', err);
    }
  }, []);

  const fetchHistory = useCallback(async () => {
    setLoadingHistory(true);
    try {
      const res = await axios.get<AlertRecord[]>('/api/alerts/history');
      setHistory(res.data);
      setHistoryPage(1);
    } catch (err) {
      console.error('Failed to load alert history', err);
    } finally {
      setLoadingHistory(false);
    }
  }, []);

  useEffect(() => {
    fetchPrefs();
    fetchHistory();
    axios.get<{ alertRunHour: number }>('/api/settings/alert-schedule')
      .then(res => setAlertRunHour(res.data.alertRunHour))
      .catch(() => setAlertRunHour(0));
  }, [fetchPrefs, fetchHistory]);

  const handleSave = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      const res = await axios.put<AlertPreferences>('/api/alerts/preferences', prefs);
      setPrefs(res.data);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      console.error('Failed to save preferences', err);
      setSaveError('Failed to save settings. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleRunNow = async () => {
    setRunning(true);
    try {
      await axios.post('/api/alerts/run');
      setTimeout(() => {
        fetchHistory();
        setRunning(false);
      }, 3000);
    } catch (err) {
      console.error('Failed to trigger alert run', err);
      setRunning(false);
    }
  };

  const setPref = (patch: Partial<AlertPreferences>) =>
    setPrefs(p => ({ ...p, ...patch }));

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Bell className="h-7 w-7 text-blue-600" />
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">Alerts</h1>
            <p className="text-sm text-gray-500">Get notified when unsubscribe or bounce thresholds are crossed</p>
          </div>
        </div>
        {user?.role === 'admin' && (
          <button
            onClick={handleRunNow}
            disabled={running}
            className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-md border border-gray-300 bg-white hover:bg-gray-50 disabled:opacity-50"
          >
            <Play className="h-4 w-4" />
            {running ? 'Running...' : 'Run Now'}
          </button>
        )}
      </div>

      {/* Settings Card */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-base font-semibold text-gray-900">Alert Settings</h2>
          <label className="flex items-center gap-2 cursor-pointer">
            <span className="text-sm text-gray-600">
              {prefs.enabled ? 'Alerts enabled' : 'Alerts disabled'}
            </span>
            <div
              onClick={() => setPref({ enabled: !prefs.enabled })}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${prefs.enabled ? 'bg-blue-600' : 'bg-gray-300'}`}
            >
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${prefs.enabled ? 'translate-x-6' : 'translate-x-1'}`} />
            </div>
          </label>
        </div>

        <div className={prefs.enabled ? '' : 'opacity-40 pointer-events-none'}>
          {/* Lookback days */}
          <div className="mb-6 flex items-center gap-4 bg-gray-100 border border-gray-300 rounded-lg px-4 py-3">
            <label className="text-sm font-semibold text-gray-900 w-48 flex-shrink-0">
              Look back
            </label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                min={1}
                max={30}
                value={prefs.lookbackDays}
                onChange={e => setPref({ lookbackDays: Math.max(1, Math.min(30, Number(e.target.value))) })}
                className="w-20 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm font-semibold text-gray-900"
              />
              <span className="text-sm font-medium text-gray-700">days (1–30)</span>
            </div>
          </div>

          <div className="space-y-4">
            {/* Alert A */}
            <AlertRow
              label="Unsubscribes from a single send"
              description="Fires when any campaign exceeds this many unsubscribes. Email will include the list of addresses."
              enabled={prefs.unsubscribePerSend.enabled}
              threshold={prefs.unsubscribePerSend.threshold}
              min={5} max={100} suffix="unsubscribes"
              onToggle={v => setPref({ unsubscribePerSend: { ...prefs.unsubscribePerSend, enabled: v } })}
              onThreshold={v => setPref({ unsubscribePerSend: { ...prefs.unsubscribePerSend, threshold: v } })}
            />

            {/* Alert B */}
            <AlertRow
              label="Unsubscribes from the same email domain"
              description="Fires when multiple contacts from the same @domain unsubscribe within the lookback window."
              enabled={prefs.unsubscribeDomain.enabled}
              threshold={prefs.unsubscribeDomain.threshold}
              min={5} max={25} suffix="from same domain"
              onToggle={v => setPref({ unsubscribeDomain: { ...prefs.unsubscribeDomain, enabled: v } })}
              onThreshold={v => setPref({ unsubscribeDomain: { ...prefs.unsubscribeDomain, threshold: v } })}
            />

            {/* Alert C */}
            <AlertRow
              label="Bounce rate threshold"
              description="Fires when a campaign's bounce rate exceeds this percentage."
              enabled={prefs.bounceRate.enabled}
              threshold={prefs.bounceRate.threshold}
              min={1} max={10} suffix="% bounce rate"
              onToggle={v => setPref({ bounceRate: { ...prefs.bounceRate, enabled: v } })}
              onThreshold={v => setPref({ bounceRate: { ...prefs.bounceRate, threshold: v } })}
            />

            {/* Alert D */}
            <AlertRow
              label="Send stuck in sending state"
              description="Fires when a queued email has been in 'sending' status for longer than this many hours."
              enabled={prefs.stuckSend?.enabled ?? false}
              threshold={prefs.stuckSend?.thresholdHours ?? 24}
              min={1} max={168} suffix="hours"
              onToggle={v => setPref({ stuckSend: { ...(prefs.stuckSend ?? { enabled: false, thresholdHours: 24 }), enabled: v } })}
              onThreshold={v => setPref({ stuckSend: { ...(prefs.stuckSend ?? { enabled: false, thresholdHours: 24 }), thresholdHours: v } })}
            />

            {/* Alert E */}
            <AlertRow
              label="Send paused for more than 24 hours"
              description="Fires when a queued email has been in 'paused' status for more than 24 hours."
              enabled={prefs.pausedSend?.enabled ?? false}
              onToggle={v => setPref({ pausedSend: { enabled: v } })}
            />
          </div>
        </div>

        <div className="mt-6 flex items-center gap-4">
          <button
            onClick={handleSave}
            disabled={saving}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
          >
            <Save className="h-4 w-4" />
            {saving ? 'Saving...' : 'Save Settings'}
          </button>
          {saved && (
            <span className="flex items-center gap-1 text-sm text-green-600">
              <CheckCircle className="h-4 w-4" />
              Saved
            </span>
          )}
          {saveError && (
            <span className="flex items-center gap-1 text-sm text-red-600">
              <XCircle className="h-4 w-4" />
              {saveError}
            </span>
          )}
          <p className="ml-auto text-xs text-gray-400">
            {alertRunHour !== null
              ? <>Alerts run automatically at{' '}
                  <span className="font-medium text-gray-500">
                    {new Date(Date.UTC(2000, 0, 1, alertRunHour)).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </span>
                  {' '}your local time. Use "Run Now" to test immediately.</>
              : 'Alerts run automatically daily. Use "Run Now" to test immediately.'
            }
          </p>
        </div>
      </div>

      {/* History Card */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-base font-semibold text-gray-900">Alert History</h2>
            {history.length > 0 && (
              <p className="text-xs text-gray-400 mt-0.5">
                Showing {Math.min((historyPage - 1) * PAGE_SIZE + 1, history.length)}–{Math.min(historyPage * PAGE_SIZE, history.length)} of {history.length}
                {history.length === 100 ? ' (max 100 kept)' : ''}
              </p>
            )}
          </div>
          <button
            onClick={fetchHistory}
            disabled={loadingHistory}
            className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${loadingHistory ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>

        {history.length === 0 ? (
          <div className="text-center py-12 text-gray-400">
            <AlertTriangle className="h-10 w-10 mx-auto mb-3 opacity-30" />
            <p className="text-sm">No alerts triggered yet.</p>
            <p className="text-xs mt-1">Configure thresholds above and run the check to see results here.</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 text-left">
                    <th className="pb-2 pr-4 text-xs font-semibold text-gray-500 uppercase">Type</th>
                    <th className="pb-2 pr-4 text-xs font-semibold text-gray-500 uppercase">Campaign</th>
                    <th className="pb-2 pr-4 text-xs font-semibold text-gray-500 uppercase">Detail</th>
                    <th className="pb-2 pr-4 text-xs font-semibold text-gray-500 uppercase">Triggered</th>
                    <th className="pb-2 text-xs font-semibold text-gray-500 uppercase">Email</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {history.slice((historyPage - 1) * PAGE_SIZE, historyPage * PAGE_SIZE).map(r => (
                    <tr key={r.id} className="hover:bg-gray-50">
                      <td className="py-3 pr-4">
                        <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${TYPE_COLORS[r.type]}`}>
                          {TYPE_LABELS[r.type]}
                        </span>
                      </td>
                      <td className="py-3 pr-4 text-gray-700 max-w-[150px] truncate">
                        {r.campaignName ?? '—'}
                      </td>
                      <td className="py-3 pr-4 text-gray-600 max-w-[300px]">
                        <span title={r.detail} className="line-clamp-2">{r.detail}</span>
                      </td>
                      <td className="py-3 pr-4 text-gray-500 whitespace-nowrap">
                        <span className="flex items-center gap-1">
                          <Clock className="h-3.5 w-3.5 flex-shrink-0" />
                          {new Date(r.triggeredAt).toLocaleString()}
                        </span>
                      </td>
                      <td className="py-3">
                        {r.emailSent
                          ? <span title="Email sent"><CheckCircle className="h-4 w-4 text-green-500" /></span>
                          : <span title="Email not sent"><XCircle className="h-4 w-4 text-gray-300" /></span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination controls */}
            {history.length > PAGE_SIZE && (
              <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-100">
                <button
                  onClick={() => setHistoryPage(p => Math.max(1, p - 1))}
                  disabled={historyPage === 1}
                  className="inline-flex items-center gap-1 px-3 py-1.5 text-sm rounded-md border border-gray-300 bg-white hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="h-4 w-4" />
                  Previous
                </button>
                <div className="flex items-center gap-1">
                  {Array.from({ length: Math.ceil(history.length / PAGE_SIZE) }, (_, i) => i + 1).map(page => (
                    <button
                      key={page}
                      onClick={() => setHistoryPage(page)}
                      className={`w-8 h-8 text-sm rounded-md ${
                        page === historyPage
                          ? 'bg-blue-600 text-white font-medium'
                          : 'border border-gray-300 bg-white hover:bg-gray-50 text-gray-700'
                      }`}
                    >
                      {page}
                    </button>
                  ))}
                </div>
                <button
                  onClick={() => setHistoryPage(p => Math.min(Math.ceil(history.length / PAGE_SIZE), p + 1))}
                  disabled={historyPage === Math.ceil(history.length / PAGE_SIZE)}
                  className="inline-flex items-center gap-1 px-3 py-1.5 text-sm rounded-md border border-gray-300 bg-white hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Next
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

interface AlertRowProps {
  label: string;
  description: string;
  enabled: boolean;
  threshold?: number;
  min?: number;
  max?: number;
  suffix?: string;
  onToggle: (v: boolean) => void;
  onThreshold?: (v: number) => void;
}

function AlertRow({ label, description, enabled, threshold, min, max, suffix, onToggle, onThreshold }: AlertRowProps) {
  const hasThreshold = threshold !== undefined && onThreshold !== undefined;
  return (
    <div className="flex items-start gap-4 p-4 rounded-lg border border-gray-100 bg-gray-50">
      <input
        type="checkbox"
        checked={enabled}
        onChange={e => onToggle(e.target.checked)}
        className="mt-0.5 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer flex-shrink-0"
      />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-gray-900">{label}</p>
        <p className="text-xs text-gray-500 mt-0.5">{description}</p>
      </div>
      {hasThreshold && (
        <div className={`flex items-center gap-2 flex-shrink-0 ${!enabled ? 'opacity-40' : ''}`}>
          <span className="text-sm text-gray-500">Alert on</span>
          <input
            type="number"
            min={min}
            max={max}
            value={threshold}
            disabled={!enabled}
            onChange={e => onThreshold!(Math.max(min!, Math.min(max!, Number(e.target.value))))}
            className="w-16 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
          />
          <span className="text-sm text-gray-500">{suffix}</span>
        </div>
      )}
    </div>
  );
}
